import { useState, useCallback, useRef } from 'react';
import { extractTextFromFile, getFileTypeEnum } from '../lib/extractDocumentContent';
import { detectCitations, extractKeyPhrases, analyzeCitationPatterns, estimateReadingLevel } from '../lib/citationDetector';
import { categorizeDocument } from '../lib/documentCategorizer';
import { convertFileToPlainTextViaCloudConvert, isCloudConvertEnabled } from '../lib/cloudConvertClient';

export interface ProcessingStage {
  id: string;
  label: string;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  message: string;
  errorDetails?: string;
}

export interface UploadResult {
  success: boolean;
  file: File;
  content: string;
  fileName: string;
  wordCount: number;
  analysis: {
    category: string;
    confidence: number;
    indicators: string[];
    suggestedFormat: string;
    contentSummary: string;
    citations: any[];
    keyPhrases: string[];
    readingLevel: string;
    complexity: number;
  };
  errorMessage?: string;
}

interface UseFailSafeUploadOptions {
  onStageUpdate?: (stages: ProcessingStage[]) => void;
  onError?: (error: string) => void;
  onSuccess?: (result: UploadResult) => void;
}

export const useFailSafeUpload = (options: UseFailSafeUploadOptions = {}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStages, setProcessingStages] = useState<ProcessingStage[]>([]);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const initializeStages = useCallback(() => {
    const stages: ProcessingStage[] = [
      {
        id: 'extract',
        label: 'Extracting text content',
        status: 'pending',
        message: 'Reading file and extracting text...',
      },
      {
        id: 'validate',
        label: 'Validating content',
        status: 'pending',
        message: 'Checking content quality...',
      },
      {
        id: 'detect-citations',
        label: 'Identifying citations',
        status: 'pending',
        message: 'Scanning for references and citations...',
      },
      {
        id: 'categorize',
        label: 'Categorizing document',
        status: 'pending',
        message: 'Analyzing document type and structure...',
      },
      {
        id: 'analyze',
        label: 'Analyzing content',
        status: 'pending',
        message: 'Processing key information and sections...',
      },
      {
        id: 'finalize',
        label: 'Finalizing results',
        status: 'pending',
        message: 'Preparing summary and recommendations...',
      },
    ];
    setProcessingStages(stages);
    options.onStageUpdate?.(stages);
    return stages;
  }, [options]);

  const updateStageStatus = useCallback(
    (stageId: string, status: ProcessingStage['status'], message?: string, errorDetails?: string) => {
      setProcessingStages((prev) => {
        const updated = prev.map((stage) =>
          stage.id === stageId 
            ? { ...stage, status, message: message || stage.message, errorDetails } 
            : stage
        );
        options.onStageUpdate?.(updated);
        return updated;
      });
    },
    [options]
  );

  const simulateUploadProgress = useCallback((file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        setIsUploading(true);
        setCurrentError(null);
        setUploadProgress(0);

        let progress = 0;
        const interval = setInterval(() => {
          progress += Math.random() * 35;
          if (progress >= 100) {
            progress = 100;
            setUploadProgress(100);
            clearInterval(interval);
            setTimeout(() => {
              setIsUploading(false);
              resolve();
            }, 300);
          } else {
            setUploadProgress(Math.floor(progress));
          }
        }, 200);

        abortControllerRef.current = new AbortController();
      } catch (error) {
        setIsUploading(false);
        reject(error);
      }
    });
  }, []);

  const processFile = useCallback(
    async (file: File): Promise<UploadResult | null> => {
      try {
        setIsProcessing(true);
        setCurrentError(null);
        const stages = initializeStages();

        // Stage 1: Extract Content
        try {
          const fileType = getFileTypeEnum(file);
          let content: string | null = null;
          let usedCloudConvert = false;

          const shouldUseCloudConvert =
            isCloudConvertEnabled && (fileType === 'pdf' || fileType === 'doc' || fileType === 'pptx' || fileType === 'xlsx');

          if (shouldUseCloudConvert) {
            updateStageStatus('extract', 'in-progress', 'Converting with CloudConvert...');
            try {
              content = await convertFileToPlainTextViaCloudConvert(file);
              usedCloudConvert = true;
            } catch (cloudError) {
              console.warn('CloudConvert conversion failed, falling back to local extraction', cloudError);
            }
          }

          if (!content) {
            updateStageStatus('extract', 'in-progress', 'Reading file and extracting text...');
            content = await extractTextFromFile(file);
          }

          if (!content || content.trim().length < 50) {
            const errorMsg = 'Could not extract meaningful content from this file. Please ensure the file contains at least 50 characters of text.';
            updateStageStatus('extract', 'error', errorMsg, 'Insufficient content extracted');
            setCurrentError(errorMsg);
            options.onError?.(errorMsg);
            setIsProcessing(false);
            return null;
          }

          updateStageStatus(
            'extract',
            'completed',
            `${usedCloudConvert ? 'Converted' : 'Text extracted'} (${content.split(/\s+/).length} words)`
          );

          // Stage 2: Validate Content
          try {
            updateStageStatus('validate', 'in-progress', 'Validating content quality...');
            await new Promise((resolve) => setTimeout(resolve, 600));
            updateStageStatus('validate', 'completed', 'Content validation passed');
          } catch (validationError) {
            const errorMsg = 'Content validation failed. Please check your file format.';
            updateStageStatus('validate', 'error', errorMsg, String(validationError));
            throw new Error(errorMsg);
          }

          // Stage 3: Detect Citations
          try {
            updateStageStatus('detect-citations', 'in-progress', 'Scanning for citations...');
            const citations = detectCitations(content);
            analyzeCitationPatterns(citations);
            await new Promise((resolve) => setTimeout(resolve, 800));
            updateStageStatus('detect-citations', 'completed', `Found ${citations.length} potential citations`);
          } catch (citationError) {
            updateStageStatus('detect-citations', 'error', 'Citation detection failed', String(citationError));
            throw new Error('Citation detection failed');
          }

          // Stage 4: Categorize Document
          let analysis;
          try {
            updateStageStatus('categorize', 'in-progress', 'Analyzing document type...');
            const keyPhrases = extractKeyPhrases(content);
            analysis = categorizeDocument(content, file.name, { patterns: [], frequency: {} });
            await new Promise((resolve) => setTimeout(resolve, 700));
            updateStageStatus('categorize', 'completed', `Document type: ${analysis.category}`);
          } catch (categError) {
            updateStageStatus('categorize', 'error', 'Categorization failed', String(categError));
            throw new Error('Document categorization failed');
          }

          // Stage 5: Analyze Content
          let readingAnalysis;
          try {
            updateStageStatus('analyze', 'in-progress', 'Analyzing content structure...');
            readingAnalysis = estimateReadingLevel(content);
            await new Promise((resolve) => setTimeout(resolve, 900));
            const wordCount = content.split(/\s+/).filter((w: string) => w.length > 0).length;
            updateStageStatus('analyze', 'completed', `${wordCount} words analyzed`);
          } catch (analyzeError) {
            updateStageStatus('analyze', 'error', 'Content analysis failed', String(analyzeError));
            throw new Error('Content analysis failed');
          }

          // Stage 6: Finalize
          try {
            updateStageStatus('finalize', 'in-progress', 'Preparing results...');
            await new Promise((resolve) => setTimeout(resolve, 500));
            updateStageStatus('finalize', 'completed', 'Ready for import');
          } catch (finalError) {
            updateStageStatus('finalize', 'error', 'Finalization failed', String(finalError));
            throw new Error('Finalization failed');
          }

          const wordCount = content.split(/\s+/).filter((w: string) => w.length > 0).length;
          const citations = detectCitations(content);
          const keyPhrases = extractKeyPhrases(content);

          const result: UploadResult = {
            success: true,
            file,
            content,
            fileName: file.name,
            wordCount,
            analysis: {
              category: analysis.category,
              confidence: analysis.confidence,
              indicators: analysis.indicators,
              suggestedFormat: analysis.suggestedFormat,
              contentSummary: analysis.contentSummary,
              citations: citations.slice(0, 20),
              keyPhrases: keyPhrases.slice(0, 15),
              readingLevel: readingAnalysis.level,
              complexity: readingAnalysis.complexity,
            },
          };

          options.onSuccess?.(result);
          setIsProcessing(false);
          return result;
        } catch (stageError) {
          const errorMsg = `Processing error: ${stageError instanceof Error ? stageError.message : 'Unknown error'}`;
          setCurrentError(errorMsg);
          options.onError?.(errorMsg);
          setIsProcessing(false);
          return null;
        }
      } catch (error) {
        const errorMsg = `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
        setCurrentError(errorMsg);
        options.onError?.(errorMsg);
        setIsProcessing(false);
        return null;
      }
    },
    [initializeStages, updateStageStatus, options]
  );

  const processFileWithRetry = useCallback(
    async (file: File, maxRetries: number = 3): Promise<UploadResult | null> => {
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const result = await processFile(file);
          if (result && result.success) {
            return result;
          }
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          }
        }
      }

      const errorMsg = `Upload failed after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`;
      setCurrentError(errorMsg);
      options.onError?.(errorMsg);
      return null;
    },
    [processFile, options]
  );

  const reset = useCallback(() => {
    setIsUploading(false);
    setIsProcessing(false);
    setUploadProgress(0);
    setProcessingStages([]);
    setCurrentError(null);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    reset();
  }, [reset]);

  return {
    isUploading,
    isProcessing,
    uploadProgress,
    processingStages,
    currentError,
    simulateUploadProgress,
    processFile,
    processFileWithRetry,
    reset,
    cancel,
  };
};
